import { useEffect } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { doClearLinkAccount, doLinkAccount, doRefreshAccount, doRemoveRedirectToLinkAccount } from 'src/actions';
import { ErrorName } from 'src/constants';
import { ErrorResponseDto } from 'src/dtos';
import { useAuthenticatedUserSelector, useFindAccountStatus, useSubmittedAccountSelector } from 'src/hooks';
import { MAlert, MButton, UnknownErrorMessage } from 'src/lib';
import { Spacing } from 'src/styles';
import { Maybe, renderLinkAccountMaximumAttemptsExceededErrorMessage } from 'src/utils';

export interface LinkAccountButtonProps {
  centered?: boolean;
}

export const LinkAccountButton = ({ centered }: LinkAccountButtonProps) => {
  const shouldRedirect = useSelector((state: any) => state.app.redirectToLinkAccount);
  const isLinkLoading: boolean = useSelector((state: any) => state.accounts.link.isLoading);
  const linkError: Maybe<ErrorResponseDto> = useSelector((state: any) => state.accounts.link.error);

  const { user } = useAuthenticatedUserSelector();
  const { account } = useSubmittedAccountSelector();
  const { isAnyDocumentRequested } = useFindAccountStatus({ skip: true });
  const dispatch = useDispatch();

  const renderLinkAccountError = () => {
    if (linkError?.name === ErrorName.LinkAccountMaximumAttemptsExceeded || !isAnyDocumentRequested) {
      return renderLinkAccountMaximumAttemptsExceededErrorMessage();
    }

    return <UnknownErrorMessage name={linkError?.name} message={linkError?.message} />;
  };

  const onLinkAccount = () => {
    dispatch(
      doLinkAccount({
        params: {
          id: user?.accounts.find(acc => acc.isRoot)?.id,
        },
        body: {
          id: account.id,
          primaryAccountHolder: {
            ssid: account.primaryAccountHolder.ssid,
            dateOfBirth: account.primaryAccountHolder.dateOfBirth,
          },
        },
      }),
    );
  };

  useEffect(() => {
    if (linkError) {
      dispatch(doRefreshAccount({ params: { id: account.id } }));
    }
  }, [linkError]);

  useEffect(() => {
    if (shouldRedirect) {
      dispatch(doRemoveRedirectToLinkAccount());
    }

    return () => {
      dispatch(doClearLinkAccount());
    };
  }, []);

  return (
    <>
      <Row>
        {(linkError || !isAnyDocumentRequested) && (
          <Col span={24}>
            <MAlert
              type='error'
              description={renderLinkAccountError()}
              icon={<ExclamationCircleOutlined />}
              showIcon
              closable={false}
              testId={'link-account-error-message'}
            />
          </Col>
        )}
      </Row>
      <Row align={centered ? 'middle' : undefined} justify={centered ? 'center' : undefined} className={Spacing.my16}>
        <Col span={24}>
          <MButton
            onClick={onLinkAccount}
            testId='link-account-btn'
            loading={isLinkLoading}
            disabled={
              isLinkLoading ||
              linkError?.name === ErrorName.LinkAccountMaximumAttemptsExceeded ||
              !isAnyDocumentRequested
            }>
            Link Account
          </MButton>
        </Col>
      </Row>
    </>
  );
};
